import { useState } from 'react';
import {
  JOBSEEKER_REPORT_REASONS,
  MESSAGE_REPORT_REASONS,
  REPORT_DETAILS_MAX,
  type ReportReason,
} from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import type { Message } from '../fixtures/domain.js';
import { ApiRequestError } from '../lib/api.js';
import { submitReport } from '../lib/facade.js';
import { useDismiss } from '../util/useDismiss.js';
import { toQuotedPreview } from './MessageQuote.js';
import styles from './ReportDialog.module.css';

const REASON_LABELS: Record<ReportReason, string> = {
  spam: 'Spam or scam',
  harassment: 'Harassment or threats',
  fake_profile: 'Fake profile or false information',
  inappropriate: 'Inappropriate or offensive',
  money_or_personal_details: 'Asks for money or personal details',
  other: 'Something else',
};

type Phase = 'editing' | 'sending' | 'failed' | 'refused' | 'sent';

/** Reports `message` when given, otherwise the jobseeker in the channel. */
export function ReportDialog({
  cid,
  message,
  counterpartName,
  onClose,
}: {
  cid: string;
  message?: Message;
  counterpartName: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [phase, setPhase] = useState<Phase>('editing');
  const sending = phase === 'sending';
  const dismiss = () => {
    if (!sending) onClose();
  };
  useDismiss(true, dismiss);

  const sent = phase === 'sent';
  const reasons = message ? MESSAGE_REPORT_REASONS : JOBSEEKER_REPORT_REASONS;
  const detailsRequired = reason === 'other';
  const canSend = reason !== null && (!detailsRequired || details.trim() !== '') && !sending;

  const send = async () => {
    if (!reason) return;
    setPhase('sending');
    try {
      await submitReport({
        channelId: cid,
        messageId: message?.id,
        reason,
        details: details.trim() || undefined,
      });
      setPhase('sent');
    } catch (error) {
      setPhase(error instanceof ApiRequestError && !error.retryable ? 'refused' : 'failed');
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={dismiss}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={sent ? styles.iconBadge : `${styles.iconBadge} ${styles.flagBadge}`}>
            <Icon name={sent ? 'check' : 'flag'} size={sent ? 20 : 18} />
          </span>
          <div className={styles.heading}>
            <h2 id="report-title" className={styles.title}>
              {sent ? 'Report sent' : message ? 'Report message' : `Report ${counterpartName}`}
            </h2>
            <p className={styles.subtitle}>
              {sent
                ? `Our team will review it. ${counterpartName} won't know you reported ${message ? 'this message' : 'them'}.`
                : `${counterpartName} won't be told. The chat stays open until you end it.`}
            </p>
          </div>
        </div>

        {sent ? (
          <div className={styles.footer}>
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className={styles.body}>
              {message && (
                <blockquote className={styles.quote}>
                  <span className={styles.quoteAuthor}>{counterpartName}</span>
                  <span className={styles.quoteText}>
                    {toQuotedPreview(message).text || 'Attachment'}
                  </span>
                </blockquote>
              )}

              <fieldset className={styles.reasons} disabled={sending}>
                <legend className={styles.label}>What&apos;s wrong?</legend>
                {reasons.map((option) => (
                  <label key={option} className={styles.reason}>
                    <input
                      type="radio"
                      name="report-reason"
                      checked={reason === option}
                      onChange={() => setReason(option)}
                    />
                    {REASON_LABELS[option]}
                  </label>
                ))}
              </fieldset>

              <label className={styles.label} htmlFor="report-details">
                {detailsRequired ? 'Tell us what happened (required)' : 'Anything else? (optional)'}
              </label>
              <textarea
                id="report-details"
                className={styles.textarea}
                rows={3}
                maxLength={REPORT_DETAILS_MAX}
                value={details}
                disabled={sending}
                onChange={(e) => setDetails(e.target.value)}
              />
              {(phase === 'failed' || phase === 'refused') && (
                <p className={styles.error} role="alert">
                  {phase === 'refused'
                    ? "This can't be reported."
                    : "Couldn't send the report. Try again."}
                </p>
              )}
            </div>

            <div className={styles.footer}>
              <Button variant="ghost" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button variant="danger" disabled={!canSend} onClick={() => void send()}>
                {sending ? 'Sending…' : 'Send report'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
